/**
 * The store-tablet app strip: one tap from The Sweet Spot into the three Android
 * apps a store also needs, so nobody has to hunt for them.
 *
 * Why these are separate apps rather than pages inside this site:
 *   - Gmail and Drive refuse to be embedded in another page (Google sends
 *     framing headers that block it), so an in-site version is not possible.
 *   - The Rippling time clock needs the tablet's camera and its own per-store
 *     link, so clocking in has to happen in the real app.
 *
 * So the strip launches each app and the tablet's Home button brings the person
 * back here. On the tablet, Scalefusion's managed home screen also shows these
 * apps; this strip exists so the common path starts from the screen staff are
 * already looking at.
 *
 * How the launch works: an Android `intent:` URL opens the app by package name,
 * and `S.browser_fallback_url` sends the browser to the web version when the app
 * is not installed, so the button always does something useful. It renders only
 * on an enrolled store tablet, because on a laptop the intent URL does nothing.
 */

import { Clock, Mail, FolderOpen } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useIsKiosk } from '@/lib/useKiosk';

/**
 * Build an Android intent URL that launches an app by package, falling back to a
 * web URL when the app is missing. Chrome on Android understands this form; other
 * browsers ignore it and follow the fallback.
 */
function launchUrl(pkg: string | null, fallbackUrl: string): string {
  if (!pkg) return fallbackUrl;
  return `intent://#Intent;package=${pkg};S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};end`;
}

type TabletApp = {
  key: string;
  label: string;
  hint: string;
  icon: LucideIcon;
  href: string;
};

/**
 * Package names. Gmail and Drive are Google's own, stable, and already installed
 * on every store tablet by the Scalefusion app allow-list.
 *
 * Rippling is deliberately left null: the Scalefusion API does not expose the
 * installed-app inventory, so its package name has to be read off a real tablet
 * (Settings > Apps > Rippling, or the Scalefusion dashboard app list) before it
 * can go here. Until it does, the button opens Rippling on the web, which
 * Android hands to the app anyway when the app claims the domain. Fill this in
 * after checking a tablet and the tile becomes a direct app launch.
 */
const GMAIL_PACKAGE = 'com.google.android.gm';
const DRIVE_PACKAGE = 'com.google.android.apps.docs';
const RIPPLING_PACKAGE: string | null = null;

const APPS: TabletApp[] = [
  {
    key: 'rippling',
    label: 'Clock in',
    hint: 'Rippling time clock',
    icon: Clock,
    href: launchUrl(RIPPLING_PACKAGE, 'https://app.rippling.com'),
  },
  {
    key: 'gmail',
    label: 'Store email',
    hint: 'Gmail',
    icon: Mail,
    href: launchUrl(GMAIL_PACKAGE, 'https://mail.google.com'),
  },
  {
    key: 'drive',
    label: 'Store files',
    hint: 'Google Drive',
    icon: FolderOpen,
    href: launchUrl(DRIVE_PACKAGE, 'https://drive.google.com'),
  },
];

export function TabletAppStrip() {
  const isKiosk = useIsKiosk();
  if (!isKiosk) return null;

  return (
    <>
      <div className="mb-2 mt-6 text-xs font-medium uppercase tracking-wide text-candy-ink-muted dark:text-candy-dark-ink-muted">
        Store apps
      </div>
      <div className="grid grid-cols-3 gap-3">
        {APPS.map((app) => (
          <a
            key={app.key}
            href={app.href}
            className="flex flex-col items-center gap-1 rounded-xl border border-candy-line bg-candy-white px-3 py-4 text-center shadow-1 hover:border-candy-pink hover:bg-candy-cream dark:border-candy-dark-line dark:bg-candy-dark-surface dark:hover:border-candy-pink-dark"
          >
            <app.icon
              className="h-6 w-6 text-candy-pink dark:text-candy-pink-dark"
              aria-hidden
            />
            <span className="text-sm font-medium text-candy-ink dark:text-candy-dark-ink">
              {app.label}
            </span>
            <span className="text-xs text-candy-ink-muted dark:text-candy-dark-ink-muted">
              {app.hint}
            </span>
          </a>
        ))}
      </div>
    </>
  );
}
